import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { unsubscribeAll } from 'src/app/common/app.helpers';
import ProjectLinks from 'src/app/common/project.links';
import { MapSearchBlockContent } from 'src/app/interfaces/map.interface';
import { CustomMarker } from 'src/app/models/custom-marker.model';
import { MapFlags } from 'src/app/models/map-flags.model';
import { PinItemBindingMediator } from '../../markers-handler-helpers/mediators/pin-item-binding.mediator';

@Component({
  selector: 'app-map-search-results',
  templateUrl: './map-search-results.component.html',
  styleUrls: ['./map-search-results.component.scss'],
})
export class MapSearchResultsComponent implements OnChanges, OnInit, AfterViewChecked, OnDestroy {
  @Input({ required: true }) public flags: MapFlags;
  @Input({ required: true }) public isAdminSession: boolean;
  @Input({ required: true }) public markers: CustomMarker[];
  @Input({ required: true }) public totalMatchesCount: number;
  @Input({ required: true }) public mediator: PinItemBindingMediator;
  @Input({ required: true }) public content: MapSearchBlockContent;

  public activeItemIndex = -1;
  public logInLink: string = ProjectLinks.LINK_LOGIN;
  public searchContainerBox: HTMLElement;

  private isFirstCheck = true;
  private destroyed = false;
  private searchResultsBox: HTMLElement;
  private subscription: Subscription;

  private changeDetector = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);

  public ngOnInit(): void {
    this.subscribeToMediatorChanges();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['markers'] && !changes['markers'].firstChange) {
      this.processClickedMarkerId();
    }
  }

  public ngAfterViewChecked(): void {
    if (!this.flags.isLoading) {
      this.searchResultsBox = document.getElementById('results-items-block');
      this.searchContainerBox = document.querySelector('.search-container'); // from parent, html

      if (this.isFirstCheck) {
        this.isFirstCheck = false;
        setTimeout(() => this.processClickedMarkerId(), 0);
      }
    }
  }

  public ngOnDestroy(): void {
    this.destroyed = true;
    unsubscribeAll([this.subscription]);
  }

  public onItemClick(id: string, index: number): void {
    this.activeItemIndex = index;
    this.mediator.onSearchItemClick(id);
  }

  public isAuthSearch(): boolean {
    const title: string = this.translateService.instant(this.content.title || ' ') as string;
    return (
      this.markers.length === 0 &&
      !this.flags.isLoggedIn &&
      (title === (this.translateService.instant('MAP_SEARCH_BLOCK_PEOPLE_TITLE_HINT') as string) ||
        title === (this.translateService.instant('MAP_SEARCH_BLOCK_ORGANIZATIONS_TITLE_HINT') as string))
    );
  }

  public get searchResultTotalCount(): string {
    if (this.totalMatchesCount > 100) {
      return '100+';
    }
    return this.markers.length.toString();
  }

  public isSearchItemVisible(container: HTMLElement, item: HTMLElement): boolean {
    const container_rect: DOMRect = container.getBoundingClientRect();
    const item_rect: DOMRect = item.getBoundingClientRect();
    return container_rect.top <= item_rect.top && container_rect.bottom >= item_rect.bottom;
  }

  /**
   * @description Resets activeItemIndex or selects a new one depend on clickedId of mediator.
   */
  private processClickedMarkerId(): void {
    if (!this.mediator.clickedId) {
      this.activeItemIndex = -1;
      !this.destroyed && this.changeDetector.detectChanges();
      return;
    }

    if (this.searchResultsBox) {
      this.highlightSearchResult();
    }
  }

  /**
   * @description Highlights the needed search result and scrolls to it in case the item
   *              with such index was found.
   */
  private highlightSearchResult(): void {
    let index: number = this.markers.findIndex((item: CustomMarker) => item.id === this.mediator.clickedId);
    if (index !== -1 && index !== this.activeItemIndex) {
      const item: HTMLElement = document.getElementById(`map-search-item-${index}`);
      if (item && this.searchContainerBox) {
        if (!this.isSearchItemVisible(this.searchContainerBox, item)) {
          this.searchContainerBox.scrollTop = item.offsetTop - this.searchContainerBox.offsetTop;
        }
        item.focus();
      } else {
        index = -1;
      }
    }

    this.activeItemIndex = index;
    !this.destroyed && this.changeDetector.detectChanges();
  }

  /**
   * @description Subscribes to markerClicked event to highlight or un-highlight the search
   *              result on it depend on current clicked id content.
   */
  private subscribeToMediatorChanges(): void {
    this.subscription = this.mediator.markerClicked.subscribe(() => this.processClickedMarkerId());
  }
}
